import { type NewUpdateWithoutTokenRequest, type QuoteDocument, QuoteLine } from '@kalos/kalos-rpc';
import { toast } from '@kalos/ui';
import { useQueryClient } from '@tanstack/react-query';
import { type FC, useCallback, useState } from 'react';

import { queryKeys } from '../../../hooks/react-query/constants';
import { useCreateQuoteLineMutation } from '../../../hooks/react-query/useQuoteLineQuery';
import { b64toBlob, ProposalClientService } from '../../../tools/helpers';
import { persistServiceNeededFromSignedQuoteLines } from '../../../utils/signedProposalServiceDescription';
import { ProposalQuickAdd } from './ProposalQuickAdd';
import { ProposalQuickSign } from './ProposalQuickSign';

interface ProposalQuickUpdateProps {
  serviceCallId: string;
  customerId: number;
  quoteDocument: QuoteDocument | null;
  onComplete: () => void;
  onCancel: () => void;
}

type WorkflowStep = 'add' | 'sign';

export const ProposalQuickUpdate: FC<ProposalQuickUpdateProps> = ({
  serviceCallId,
  customerId,
  quoteDocument,
  onComplete,
  onCancel,
}) => {
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState<WorkflowStep>('add');
  const [lineItemsToAdd, setLineItemsToAdd] = useState<QuoteLine[]>([]);
  const [signature, setSignature] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mutations
  const createQuoteLineMutation = useCreateQuoteLineMutation();

  // Handle continue from add step
  const handleContinueFromAdd = useCallback((items: QuoteLine[]) => {
    setLineItemsToAdd(items);
    setCurrentStep('sign');
  }, []);

  // Handle back from sign step - clear signature when going back
  const handleBackToAdd = useCallback(() => {
    setSignature('');
    setCurrentStep('add');
  }, []);

  // Handle signature change
  const handleSignatureChange = useCallback((sig: string) => {
    setSignature(sig);
  }, []);

  // Handle final submission
  const handleSubmit = useCallback(async () => {
    if (!signature || lineItemsToAdd.length === 0) {
      toast({
        title: 'Missing Information',
        description: 'Please ensure you have added items and provided a signature.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Step 1: Create all quote lines
      const createdQuoteLines: QuoteLine[] = [];
      for (const item of lineItemsToAdd) {
        const newQuoteLine = QuoteLine.create({
          ...item,
          isActive: 1,
          warranty: 2,
          jobNumber: serviceCallId,
          forUser: customerId,
          quoteDocumentId: quoteDocument?.id,
        });

        const created = await createQuoteLineMutation.mutateAsync(newQuoteLine);
        if (created) {
          createdQuoteLines.push(created);
        }
      }

      // Step 2: Prepare only the newly created quote lines for the update
      const allQuoteLines = [
        // Newly created quote lines - mark as accepted
        ...createdQuoteLines.map((quoteLine) => ({
          ...quoteLine,
          quoteStatus: 1,
          fieldMask: ['QuoteStatus'],
        })),
      ];

      // Step 3: Create the update request
      const updateRequest: NewUpdateWithoutTokenRequest = {
        jobId: parseInt(serviceCallId, 10),
        quoteDocumentId: quoteDocument?.id ?? 0,
        notify: true, // Send notification email
        quoteLines: allQuoteLines,
        overrideNote: '',
      };

      if (signature) {
        // Strip the data URL prefix from signature before converting
        const base64Data = signature.replace(/^data:image\/\w+;base64,/, '');
        const signatureBlob = b64toBlob(base64Data, 'image/png');
        const signatureArrayBuffer = await signatureBlob.arrayBuffer();
        updateRequest.signature = {
          data: new Uint8Array(signatureArrayBuffer),
        };
      }

      const proposalResponse = await ProposalClientService.newUpdateWithoutToken(updateRequest);
      const jobId = parseInt(serviceCallId, 10);
      try {
        await persistServiceNeededFromSignedQuoteLines({
          acceptedLines: createdQuoteLines,
          proposalResponse: proposalResponse ?? undefined,
          fallbackEventId: jobId,
        });
      } catch (persistErr) {
        console.error('Could not update Services Needed from signed proposal lines', persistErr);
      }

      await queryClient.invalidateQueries({ queryKey: [queryKeys.events.root] });

      toast({
        title: 'Success',
        description: `Successfully added ${createdQuoteLines.length} line item(s) and recorded approval.`,
        variant: 'success',
      });

      // Reset the workflow to the first step for next use
      setCurrentStep('add');
      setLineItemsToAdd([]);
      setSignature('');

      onComplete();
    } catch (error) {
      console.error('Error submitting proposal quick update:', error);
      toast({
        title: 'Error',
        description: 'Failed to update proposal. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    signature,
    lineItemsToAdd,
    serviceCallId,
    customerId,
    createQuoteLineMutation,
    onComplete,
    quoteDocument?.id,
    queryClient,
  ]);

  return (
    <div className="mx-auto max-w-4xl">
      {currentStep === 'add' && (
        <ProposalQuickAdd
          serviceCallId={serviceCallId}
          initialItems={lineItemsToAdd}
          onContinue={handleContinueFromAdd}
          onCancel={onCancel}
        />
      )}

      {currentStep === 'sign' && (
        <ProposalQuickSign
          lineItems={lineItemsToAdd}
          signature={signature}
          onSignatureChange={handleSignatureChange}
          onSubmit={handleSubmit}
          onBack={handleBackToAdd}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
};
